import { useMemo, useState } from 'react';
import { downloadCatalogXlsx } from './catalogExportApi.js';

/**
 * Modal de download do catálogo em Excel (.xlsx).
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   slug: string,
 *   customerToken: string,
 *   sections?: Array<{ secao: string }>,
 * }} props
 */
export function CatalogExportModal({ open, onClose, slug, customerToken, sections = [] }) {
  const [mode, setMode] = useState('all');
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sectionNames = useMemo(() => {
    const names = (Array.isArray(sections) ? sections : [])
      .map((s) => (s && typeof s.secao === 'string' ? s.secao.trim() : ''))
      .filter((s) => s !== '');
    return Array.from(new Set(names));
  }, [sections]);

  if (!open) return null;

  function toggleSection(name) {
    setSelected((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  const chosen = sectionNames.filter((n) => selected[n]);

  async function handleDownload() {
    setError('');
    if (mode === 'some' && chosen.length === 0) {
      setError('Selecione ao menos uma categoria ou escolha "Catálogo completo".');
      return;
    }
    setBusy(true);
    try {
      const cats = mode === 'some' ? chosen : [];
      const { blob, filename } = await downloadCatalogXlsx(slug, customerToken, cats);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      setError(e?.message || 'Não foi possível baixar o catálogo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <div>
            <h3 className="font-display text-lg font-semibold">Baixar catálogo em Excel</h3>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Preencha a coluna <strong>Quantidade</strong> e reenvie pelo botão de pedido por lista.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">
            Fechar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 px-4 py-3 dark:border-zinc-700">
              <input
                type="radio"
                name="catalog-export-mode"
                className="mt-0.5 h-4 w-4 accent-[var(--st-600)]"
                checked={mode === 'all'}
                onChange={() => setMode('all')}
              />
              <span>
                <span className="block text-sm font-medium">Catálogo completo</span>
                <span className="block text-xs text-zinc-500 dark:text-zinc-400">Todos os produtos disponíveis.</span>
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-zinc-200 px-4 py-3 dark:border-zinc-700">
              <input
                type="radio"
                name="catalog-export-mode"
                className="mt-0.5 h-4 w-4 accent-[var(--st-600)]"
                checked={mode === 'some'}
                onChange={() => setMode('some')}
                disabled={sectionNames.length === 0}
              />
              <span>
                <span className="block text-sm font-medium">Somente algumas categorias</span>
                <span className="block text-xs text-zinc-500 dark:text-zinc-400">
                  {sectionNames.length === 0 ? 'Sem categorias disponíveis.' : 'Escolha abaixo as seções desejadas.'}
                </span>
              </span>
            </label>
          </div>

          {mode === 'some' && sectionNames.length > 0 && (
            <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-2xl border border-zinc-100 p-3 sm:grid-cols-2 dark:border-zinc-800">
              {sectionNames.map((name) => (
                <label key={name} className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800/60">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--st-600)]"
                    checked={Boolean(selected[name])}
                    onChange={() => toggleSection(name)}
                  />
                  <span className="truncate">{name}</span>
                </label>
              ))}
            </div>
          )}

          {error && (
            <p className="mt-3 rounded-xl bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-2xl bg-[var(--st-600)] px-5 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Gerando…' : 'Baixar Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}
